// Das Modell bündelt alles, was die Zeichenschichten brauchen: sichtbarer
// Ausschnitt, Skalen und die Zugriffsfunktionen auf y-Positionen. Die Schichten
// selbst rechnen nicht mehr, sie zeichnen nur.
//
// In der Änderungsansicht ist die Nulllinie der beschlossene Stand, also
// anchor + pending. Der noch nicht wirksame Schritt erscheint dadurch als
// Abstand der Ankerlinie zur Null und nicht als Sprung der PIPR-Linie.

import * as d3 from "d3";

import { computeLayout } from "./layout";
import type { Layout } from "./layout";
import type { PiprBundle, PiprForward } from "../types";

export type RangeKey = "m1" | "m3" | "y1" | "all";
export type ModeKey = "rate" | "delta";
export type Hover = { kind: "hist"; i: number } | { kind: "fwd"; i: number } | null;

export type Svg = d3.Selection<SVGSVGElement, unknown, null, undefined>;
export type G = d3.Selection<SVGGElement, unknown, null, undefined>;

export const DAY_MS = 86400000;

export interface Day {
    i: number;
    date: Date;
    /** Index des an diesem Tag bepreisten Meetings in `data.meetings` */
    meetingIdx: number;
    anchor: number;
    /** beschlossen, an diesem Tag noch nicht wirksam */
    pending: number;
    implied: number;
    p: number[];
    modal: number;
    flagged: boolean;
}

export interface Model {
    layout: Layout;
    isRate: boolean;

    deltas: number[];
    /** Abstand zweier Bins in bp */
    step: number;

    /** sichtbarer Ausschnitt der Tagesreihe */
    vis: Day[];
    x: d3.ScaleTime<number, number>;
    /** Breite einer Tagesspalte in px */
    dayW: number;
    spanDays: number;
    visMeetings: Date[];
    /** true, sobald ein sichtbarer Tag einen offenen Schritt trägt */
    hasPending: boolean;

    rowH: number;
    cellH: number;
    yRate: d3.ScaleLinear<number, number>;
    yTicks: number[];
    yDelta: (bp: number) => number;

    yCellHist: (d: Day, j: number) => number;
    yAnchor: (d: Day) => number;
    yDecided: (d: Day) => number;
    yPipr: (d: Day) => number;

    fwd: PiprForward;
    fwdModal: number[];
    nF: number;
    slot: number;
    colW: number;
    xF: (i: number) => number;
    yFwdCell: (i: number, j: number) => number;
    yFwdPoint: (i: number) => number;
    yFwdStart: number;
}

/** Tagesreihe einmal aus dem Bundle aufbereiten. */
export function buildDays(data: PiprBundle): Day[] {
    const h = data.history;
    return d3.range(h.n).map((i) => {
        const p = h.p[i];
        let modal = 0;
        for (let j = 1; j < p.length; j++) if (p[j] > p[modal]) modal = j;
        return {
            i,
            date: new Date(h.ts[i] * 1000),
            meetingIdx: h.meetingIdx[i],
            anchor: h.anchorBp[i],
            pending: h.pendingBp[i] ?? 0,
            implied: h.impliedBp[i],
            p,
            modal,
            flagged: h.flags[i] !== 0,
        };
    });
}

export function buildMeetingDates(data: PiprBundle): Date[] {
    return data.meetings.map((m) => new Date(m.decisionTs * 1000));
}

export function buildFwdModal(data: PiprBundle): number[] {
    return data.forward.meetings.map((m) => {
        let k = 0;
        for (let j = 1; j < m.p.length; j++) if (m.p[j] > m.p[k]) k = j;
        return k;
    });
}

const shift = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS);

/** Startzeitpunkt des sichtbaren Fensters für eine Zoomstufe. */
export function windowStart(days: Day[], meetings: Date[], range: RangeKey): Date {
    const firstDay = days[0].date;
    const lastDay = days[days.length - 1].date;
    const past = meetings.filter((d) => d <= lastDay);

    let start: Date;
    if (range === "m1" && past.length >= 1) start = shift(past[past.length - 1], -21);
    else if (range === "m3" && past.length >= 3) start = shift(past[past.length - 3], -7);
    else if (range === "y1") start = shift(lastDay, -365);
    else start = firstDay;

    return start < firstDay ? firstDay : start;
}

export interface ModelInput {
    data: PiprBundle;
    days: Day[];
    meetingDates: Date[];
    fwdModal: number[];
    width: number;
    range: RangeKey;
    mode: ModeKey;
}

export function buildModel(input: ModelInput): Model | null {
    const { data, days, meetingDates, fwdModal, width, range, mode } = input;
    if (days.length === 0 || width === 0) return null;

    const layout = computeLayout(width);
    const { plotTop, plotBottom, marginLeft, split, fwdW } = layout;

    const deltas = data.history.deltas;
    const step = deltas.length > 1 ? deltas[1] - deltas[0] : 25;
    const fwd = data.forward;

    const lastDay = days[days.length - 1].date;
    const start = windowStart(days, meetingDates, range);

    const x = d3.scaleTime().domain([start, lastDay]).range([marginLeft, split]);
    const vis = days.filter((d) => d.date >= start && d.date <= lastDay);
    if (vis.length === 0) return null;

    // Spaltenbreite aus dem tatsächlichen Abstand der Punkte, nicht aus einer
    // angenommenen Tageslänge — die Cut-Abstände schwanken an DST-Tagen.
    const dayW =
        vis.length > 1
            ? Math.max(1, (x(vis[vis.length - 1].date) - x(vis[0].date)) / (vis.length - 1) - 0.4)
            : 6;

    const lows: number[] = [];
    const highs: number[] = [];
    vis.forEach((d) => {
        const decided = d.anchor + d.pending;
        lows.push(Math.min(d.anchor, decided) + deltas[0]);
        highs.push(Math.max(d.anchor, decided) + deltas[deltas.length - 1]);
    });
    fwd.meetings.forEach((m) => {
        lows.push(m.baseBp + deltas[0]);
        highs.push(m.baseBp + deltas[deltas.length - 1]);
    });

    const yRate = d3
        .scaleLinear()
        .domain([(d3.min(lows) as number) - 18, (d3.max(highs) as number) + 18])
        .range([plotBottom, plotTop]);

    const rowH = (plotBottom - plotTop) / deltas.length;
    const yRow = d3
        .scaleLinear()
        .domain([0, deltas.length - 1])
        .range([plotBottom - rowH / 2, plotTop + rowH / 2]);
    const yDelta = (bp: number) => yRow((bp - deltas[0]) / step);

    const isRate = mode === "rate";
    const cellH = isRate ? Math.abs(yRate(0) - yRate(step)) - 2 : rowH - 5;

    const nF = fwd.meetings.length;
    const slot = fwdW / nF;

    return {
        layout,
        isRate,
        deltas,
        step,
        vis,
        x,
        dayW,
        spanDays: (lastDay.getTime() - start.getTime()) / DAY_MS,
        visMeetings: meetingDates.filter((d) => d >= start && d <= lastDay),
        hasPending: vis.some((d) => d.pending !== 0),

        rowH,
        cellH,
        yRate,
        yTicks: yRate.ticks(layout.isNarrow ? 4 : 6),
        yDelta,

        yCellHist: (d, j) =>
            isRate ? yRate(d.anchor + d.pending + deltas[j]) : yDelta(deltas[j]),
        yAnchor: (d) => (isRate ? yRate(d.anchor) : yDelta(-d.pending)),
        yDecided: (d) => (isRate ? yRate(d.anchor + d.pending) : yDelta(0)),
        yPipr: (d) =>
            isRate ? yRate(d.implied) : yDelta(d.implied - d.anchor - d.pending),

        fwd,
        fwdModal,
        nF,
        slot,
        colW: Math.max(10, Math.min(36, slot - 10)),
        xF: (i) => split + slot * (i + 0.5),
        yFwdCell: (i, j) =>
            isRate ? yRate(fwd.meetings[i].baseBp + deltas[j]) : yDelta(deltas[j]),
        yFwdPoint: (i) =>
            isRate
                ? yRate(fwd.meetings[i].rateBp)
                : yDelta(fwd.meetings[i].rateBp - fwd.meetings[i].baseBp),
        yFwdStart: isRate ? yRate(fwd.anchorBp + fwd.pendingBp) : yDelta(0),
    };
}